import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function ProfileSettings() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState("");
  const [picture, setPicture] = useState("");
  const [brandColor, setBrandColor] = useState("#D4AF37");
  const [logoUrl, setLogoUrl] = useState("");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setPicture(user.picture || "");
      setBrandColor(user.brand_color || "#D4AF37");
      setLogoUrl(user.logo_url || "");
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { name, picture, logo_url: logoUrl };
      if (user?.role === "merchant") body.brand_color = brandColor;
      await api.put("/profile", body);
      toast.success("Profile updated");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    setChanging(true);
    try {
      await api.post("/profile/password", { current_password: curPw, new_password: newPw });
      toast.success("Password changed");
      setCurPw(""); setNewPw("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setChanging(false); }
  };

  if (!user) return null;

  return (
    <main data-testid="profile-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
      <div className="text-xs uppercase tracking-[0.3em] text-gold">Atelier</div>
      <h1 className="font-display text-5xl tracking-tighter mt-3">Profile</h1>

      <section className="mt-14">
        <h2 className="font-display text-2xl tracking-tight mb-8">Identity</h2>
        <form onSubmit={save} className="space-y-8">
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Display name</label>
            <input data-testid="profile-name" required value={name} onChange={(e) => setName(e.target.value)} className="lux-input mt-2" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Avatar URL (optional)</label>
            <input data-testid="profile-picture" value={picture} onChange={(e) => setPicture(e.target.value)} placeholder="https://…" className="lux-input mt-2" />
          </div>

          {user.role === "merchant" && (
            <>
              <div className="pt-4 border-t hairline">
                <div className="font-display text-xl tracking-tight mb-6">Brand · for pay pages</div>
                <div className="grid grid-cols-12 gap-6 items-end">
                  <div className="col-span-8">
                    <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Logo URL</label>
                    <input data-testid="profile-logo" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://yourbrand.com/logo.png" className="lux-input mt-2" />
                  </div>
                  <div className="col-span-4 flex items-end gap-3">
                    <div className="flex-1">
                      <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Accent</label>
                      <input data-testid="profile-brand-color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)} placeholder="#D4AF37" className="lux-input mt-2 font-mono uppercase" />
                    </div>
                    <input type="color" value={brandColor} onChange={(e) => setBrandColor(e.target.value.toUpperCase())} className="h-11 w-12 bg-transparent border hairline-strong cursor-pointer" />
                  </div>
                </div>
                {logoUrl && (
                  <div className="mt-6 lux-card p-6 flex items-center gap-4">
                    <img src={logoUrl} alt="logo preview" className="h-10 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
                    <div>
                      <div className="text-xs text-muted-fg uppercase tracking-widest">Preview</div>
                      <div className="font-display text-xl" style={{ color: brandColor }}>{name || "Your Brand"}</div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <button data-testid="profile-save" disabled={saving} className="btn-gold">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </form>
      </section>

      <section className="mt-20 pt-12 border-t hairline">
        <h2 className="font-display text-2xl tracking-tight mb-8">Password</h2>
        <form onSubmit={changePw} className="space-y-8 max-w-md">
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Current password</label>
            <input data-testid="profile-current-pw" type="password" required value={curPw} onChange={(e) => setCurPw(e.target.value)} className="lux-input mt-2" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">New password</label>
            <input data-testid="profile-new-pw" type="password" required minLength={6} value={newPw} onChange={(e) => setNewPw(e.target.value)} className="lux-input mt-2" />
          </div>
          <button data-testid="profile-change-pw" disabled={changing} className="btn-ghost">
            {changing ? "Changing…" : "Change password"}
          </button>
        </form>
      </section>
    </main>
  );
}
